import React, { useState, useEffect } from 'react';
import { LockupStatusCard } from './components/LockupStatusCard';
import { Dashboard } from './components/Dashboard';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { WalletTransfer } from './components/WalletTransfer';
import { Staking } from './components/Staking';
import { Events } from './components/Events';
import { BridgePage } from './pages/BridgePage';
import { GameArena } from './components/GameArena';
import { SplashScreen } from './components/SplashScreen';
import { PWAInstaller } from './components/PWAInstaller';
import { InAppBrowserNotice, SHOW_INAPP_GUIDE_EVENT } from './components/InAppBrowserNotice';
import { detectInAppBrowser } from './utils/inappBrowser';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { Home, Wallet, Calendar, Send, TrendingUp, Gift, ArrowLeftRight, Smartphone, Gamepad2 } from 'lucide-react';
import { Rewards } from './components/Rewards';
import { SignupCompletePopup } from './components/SignupCompletePopup';
import { Settings } from './components/Settings';
import WalletTest from './components/WalletTest';
import { AdminPanel } from './components/AdminPanel';
import { useSession } from './hooks/useSession';
import { useWalletData } from './hooks/useWalletData';
import { i18n } from './utils/i18n';
import { getDefaultDarkMode } from './utils/brand';

function readDarkMode(): boolean {
  try {
    const saved = localStorage.getItem('handy-dark-mode');
    if (saved !== null) return JSON.parse(saved);
  } catch {
    /* ignore */
  }
  return getDefaultDarkMode();
}

// ===== 타입 정의 =====
export interface User {
  id: number;
  nickname: string;
  walletAddress: string;
  referralCode: string;
  referredBy: string | null;
  balance: number;
  pendingSignupBonus?: number;
  createdAt: string;
}

export interface Transaction {
  id?: number;
  type?: string;
  amount?: number;
  createdAt?: string;
  // 블록체인 조회 시 사용되는 필드
  hash?: string;
  from?: string | null;
  to?: string | null;
  value?: string;
  blockNumber?: number;
  timestamp?: number;
  status?: string;
  source?: string;
}

export default function App() {
  // ===== RPC API 훅 사용 =====
  const { user, accessToken, isLoading: sessionLoading, login, signup, logout } = useSession();
  const { avaxBalance, erc20Balances, vttnBalance, transactions, isLoading: walletLoading, refresh } = useWalletData(user?.walletAddress);
  const [showSignupComplete, setShowSignupComplete] = useState(
    () => localStorage.getItem('handy-signup-bonus-popup') !== null
  );
  const [signupBonusAmount, setSignupBonusAmount] = useState(
    () => Number(localStorage.getItem('handy-signup-bonus-popup') || 0)
  );
  
  // ===== 상태 관리 =====
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState('login');
  // 게임 API 호출용 인증 헤더
  const authHeaders = () => {
    const t = localStorage.getItem('handy-jwt-token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  const [activeTab, setActiveTab] = useState('dashboard');
  const [walletSub, setWalletSub] = useState<'wallet' | 'bridge'>('wallet');
  // 스테이킹에서 «구매하러 가기» 를 누르면 지갑의 구매 화면으로 보낸다.
  useEffect(() => {
    const go = () => {
      setActiveTab('transfer');
      try { setWalletSub('wallet'); } catch {}
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent('handy:open-buy')); } catch {}
      }, 150);
    };
    window.addEventListener('handy:go-buy', go);
    const goGame = () => setActiveTab('game');
    window.addEventListener('handy:go-game', goGame);
    const goRewards = () => setActiveTab('rewards');
    window.addEventListener('handy:go-rewards', goRewards);
    return () => { window.removeEventListener('handy:go-buy', go); window.removeEventListener('handy:go-game', goGame); window.removeEventListener('handy:go-rewards', goRewards); };
  }, []);

  const [isAdmin, setIsAdmin] = useState(false);
  const [eventSettings, setEventSettings] = useState({
    startDate: '2024-12-25T20:00:00',
    endDate: '2024-12-26T08:00:00'
  });
  const [darkMode, setDarkMode] = useState(readDarkMode);
  const darkModeBootRef = React.useRef(true);
  
  // PWA 설치 상태
  const [showSplash, setShowSplash] = useState(true);
  const [showPWAInstaller, setShowPWAInstaller] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(() => (window as any).__handyInstallPrompt || null);
  const triggerInstall = async () => {
    // 브라우저가 설치 프롬프트를 주지 않는 경우(iOS Safari 등)에도 배너는 항상 노출하고
    // 여기서 수동 설치 방법을 안내한다.
    // [2026-09-12 긴급] 카카오톡 등 인앱 브라우저는 PWA 설치를 지원하지 않는다.
    // 여기서 "브라우저 메뉴(⋮) → 앱 설치" 같은 안내를 하면 그런 메뉴가 없어서
    // 사용자가 더 헤맨다. 인앱이면 곧바로 외부 브라우저로 넘겨준다.
    const inApp = detectInAppBrowser();
    if (inApp) {
      window.dispatchEvent(new Event(SHOW_INAPP_GUIDE_EVENT));
      return;
    }

    const prompt = installPrompt || (window as any).__handyInstallPrompt;
    if (!prompt) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      // 이미 설치돼 있으면 브라우저가 설치 프롬프트를 아예 주지 않는다.
      // 그 경우를 구분해서 정확히 안내한다.
      let alreadyInstalled = false;
      try {
        const getInstalled = (navigator as any).getInstalledRelatedApps;
        if (typeof getInstalled === 'function') {
          const apps = await getInstalled.call(navigator);
          alreadyInstalled = Array.isArray(apps) && apps.length > 0;
        }
      } catch { /* 무시 */ }

      if (alreadyInstalled) {
        toast.info('이미 설치되어 있습니다. 홈 화면의 핸디 월렛 아이콘으로 실행하세요.');
      } else {
        toast.info(
          isIOS
            ? '사파리 하단 공유 버튼 → "홈 화면에 추가"를 눌러 설치하세요.'
            : '이미 설치되어 있거나, 브라우저 메뉴(⋮) → "앱 설치"로 설치할 수 있습니다.'
        );
      }
      return;
    }
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice?.outcome === 'accepted') {
        (window as any).__handyInstallPrompt = null;
        setInstallPrompt(null);
        toast.success('설치가 완료되면 홈 화면에서 앱으로 실행하세요.');
      }
    } catch { /* 무시 */ }
  };
  const [isStandalone] = useState<boolean>(() => {
    try {
      return window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as any).standalone === true;
    } catch { return false; }
  });

  // 실제 서비스워커 등록 + 브라우저 네이티브 설치 프롬프트(beforeinstallprompt) 캡처.
  // 이전엔 서비스워커를 일부러 해제하고 가짜 진행바만 보여줬음 - 실제 설치가 전혀 안 되고 있었음.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).__handyInstallPrompt = e;
      setInstallPrompt(e);
    };
    // index.html이 먼저 잡아둔 경우를 위한 동기화
    const handleInstallReady = () => setInstallPrompt((window as any).__handyInstallPrompt || null);
    const handleInstalled = () => setInstallPrompt(null);
    if ((window as any).__handyInstallPrompt) setInstallPrompt((window as any).__handyInstallPrompt);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('handy-install-ready', handleInstallReady);
    window.addEventListener('handy-app-installed', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('handy-install-ready', handleInstallReady);
      window.removeEventListener('handy-app-installed', handleInstalled);
    };
  }, []);

  // 회원가입 직후 메인 진입 시 에어드롭 지급 안내 팝업을 띄운다.
  // handleRegister에서 localStorage에 플래그를 심어두고, 메인이 렌더되는 시점에 확인한다.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const flag = localStorage.getItem('handy-signup-bonus-popup');
    if (flag !== null) {
      const amount = Number(flag) || Number(user.pendingSignupBonus || 0);
      setSignupBonusAmount(amount);
      setShowSignupComplete(true);
    }
  }, [isAuthenticated, user]);

  // ===== SSE 연결 관리 =====
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const token = localStorage.getItem('handy-jwt-token');
    if (!token) return;

    // Mock SSE 연결 (실제 환경에서는 실제 SSE 사용)
    const mockSSE = setInterval(() => {
      // 모의 실시간 업데이트
    }, 30000);

    return () => {
      clearInterval(mockSSE);
    };
  }, [isAuthenticated, user]);

  // ===== 스플래시 화면 및 PWA 설치 관리 =====
  useEffect(() => {
    // PWA 설치 상태 체크
    const pwaInstalled = localStorage.getItem('handy-pwa-installed');
    
    if (pwaInstalled) {
      setIsPWAInstalled(true);
      setShowSplash(false);
      setShowPWAInstaller(false);
    } else {
      // 스플래시 화면 타이머
      const splashTimer = setTimeout(() => {
        setShowSplash(false);
        setShowPWAInstaller(true);
      }, 3000);

      return () => clearTimeout(splashTimer);
    }
  }, []);

  // ===== 다크 모드 초기화 및 자동 로그인 체크 =====
  useEffect(() => {
    if (!isPWAInstalled && (showSplash || showPWAInstaller)) return;

    // RPC API를 통한 자동 로그인 체크
    if (accessToken && user) {
      setIsAuthenticated(true);
      setCurrentView('dashboard');
    }
  }, [isPWAInstalled, showSplash, showPWAInstaller, accessToken, user]);

  // ===== 다크 모드 효과 적용 =====
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    if (darkModeBootRef.current) {
      darkModeBootRef.current = false;
      return;
    }
    localStorage.setItem('handy-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);


  // ===== 인증 함수들 =====
  const handleLogin = async (nickname: string, pin: string) => {
    try {
      const result = await login(nickname, pin);
      
      if (result.success) {
        setIsAuthenticated(true);
        setCurrentView('dashboard');
        setActiveTab('dashboard');
        return { success: true };
      } else {
        throw new Error(result.error || '로그인 실패');
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      throw error;
    }
  };

  const handleRegister = async (nickname: string, pin: string, phoneNumber?: string, fullName?: string) => {
    setIsLoading(true);

    try {
      // 입력값 검증
      if (!nickname.trim() || !pin.trim()) {
        throw new Error('Please enter both nickname and PIN.');
      }

      if (pin.length < 6) {
        throw new Error('PIN은 최소 6자리여야 합니다.');
      }

      console.log('Attempting register with:', { 
        nickname: nickname.trim(), 
        pin: pin.trim(), 
        phoneNumber: phoneNumber?.trim()
      }); // 디버깅용

      const result = await signup(nickname.trim(), pin.trim(), phoneNumber?.trim(), fullName?.trim());

      console.log('Register response:', result); // 디버깅용

      if (result.success) {
        // JWT 토큰 저장
        if (result.accessToken) {
          localStorage.setItem('handy-jwt-token', result.accessToken);
        }
        if (result.refreshToken) {
          localStorage.setItem('handy-refresh-token', result.refreshToken);
        }
        
        // 회원가입 성공 후 바로 대시보드로 이동
        setIsAuthenticated(true);
        setCurrentView('dashboard');
        setActiveTab('dashboard');
        const bonus = Number(result.user?.pendingSignupBonus || 0);
        localStorage.setItem('handy-signup-bonus-popup', String(bonus));
        setSignupBonusAmount(bonus);
        setShowSignupComplete(true);
        return { success: true, message: '회원가입이 완료되었습니다.' };
      } else {
        throw new Error(result.error || 'Registration failed.');
      }
    } catch (error) {
      console.error('회원가입 오류:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    console.log('Logging out user'); // 디버깅용
    
    // 로그아웃 처리
    logout();
    
    // 상태 초기화
    setIsAuthenticated(false);
    setCurrentView('login');
    setActiveTab('dashboard');
    setIsLoading(false);
    
    console.log('Logout completed'); // 디버깅용
  };

  // ===== API base 설정 =====
  const rawApiBase = (import.meta as any)?.env?.VITE_API_URL ?? 'https://wallet.handy.app';
  const apiBase = rawApiBase.endsWith('/') ? rawApiBase.slice(0, -1) : rawApiBase;

  // ===== 지갑 함수들 =====
  const updateBalance = async (newBalance: number) => {
    try {
      // handy-jwt-token 또는 accessToken 사용
      const token = localStorage.getItem('handy-jwt-token') || accessToken;
      if (!token) {
        // 토큰이 없어도 로컬 상태만 업데이트 (에러 로그 제거)
        return;
      }

      const response = await fetch(`${apiBase}/api/handy/users/balance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ balance: newBalance })
      });

      if (!response.ok) {
        throw new Error('잔고 업데이트 실패');
      }

      const result = await response.json();
      console.log('잔고 업데이트 성공:', result);
    } catch (error) {
      console.error('잔고 업데이트 오류:', error);
    }
  };

  const addTransaction = async (transaction: Omit<Transaction, 'id'>) => {
    try {
      // handy-jwt-token 또는 accessToken 사용
      const token = localStorage.getItem('handy-jwt-token') || accessToken;
      if (!token) {
        // 토큰이 없어도 로컬 상태만 업데이트 (에러 로그 제거)
        return;
      }

      const response = await fetch(`${apiBase}/api/handy/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transaction)
      });

      if (!response.ok) {
        throw new Error('트랜잭션 추가 실패');
      }

      const result = await response.json();
      console.log('트랜잭션 추가 성공:', result);
      return result;
    } catch (error) {
      console.error('트랜잭션 추가 오류:', error);
    }
  };

  // ===== 관리자 함수들 =====
  const handleAdminLogin = (password: string) => {
    if (password === '3bebe@MAMA') {
      setIsAdmin(true);
      return true;
    }
    return false;
  };

  const handleEventSettingsUpdate = (settings: typeof eventSettings) => {
    setEventSettings(settings);
  };

  // ===== 다크 모드 토글 =====
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // ===== PWA 설치 핸들러 =====
  const handlePWAInstallComplete = () => {
    localStorage.setItem('handy-pwa-installed', 'true');
    setIsPWAInstalled(true);
    setShowPWAInstaller(false);
  };

  // ===== 렌더링 =====
  // 어드민 패널 (숨김 URL: /handyadmin)
  if (window.location.pathname === '/handyadmin') {
    return (
      <>
        <AdminPanel />
        <Toaster />
      </>
    );
  }

  // 스플래시 화면
  if (showSplash) {
    return <SplashScreen />;
  }

  // PWA 설치 화면
  if (showPWAInstaller) {
    return (
      <>
        <PWAInstaller onInstallComplete={handlePWAInstallComplete} installPrompt={installPrompt} />
        <InAppBrowserNotice />
        <Toaster />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-background">
          {/* Mobile Frame - Full screen for PWA */}
          <div className="w-full min-h-screen bg-background relative">
            {/* Content */}
            <div className="h-full overflow-auto scrollbar-hide">
              {currentView === 'login' ? (
                <Login 
                  onLogin={handleLogin}
                  onSwitchToRegister={() => setCurrentView('register')}
                  isLoading={sessionLoading}
                />
              ) : (
                <Register 
                  onRegister={handleRegister}
                  onSwitchToLogin={() => setCurrentView('login')}
                  isLoading={sessionLoading}
                />
              )}
            </div>
          </div>
        </div>
        {/* 카톡 등 인앱 브라우저로 들어온 경우 설치 방법 안내 팝업 */}
        <InAppBrowserNotice />
        <Toaster />
      </>
    );
  }

  // 세션 로딩 중일 때만 로딩 화면 표시 (user가 없어도 메뉴 접근 허용)
  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">사용자 정보를 불러오는 중입니다...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        {/* Mobile Frame - Full screen for PWA */}
        <div className="w-full min-h-screen bg-background relative" style={{ maxWidth: '100vw', height: '100vh' }}>
          {/* Content */}
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-auto scrollbar-hide" style={{ paddingBottom: '80px' }}>
              {activeTab === 'dashboard' && (
                <Dashboard 
                  user={user} 
                  transactions={transactions}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                  onNavigateToNews={() => setActiveTab('events')}
                  onNavigateToSettings={() => setActiveTab('settings')}
                  onNavigateToEvents={() => setActiveTab('events')}
                  onInstallApp={isStandalone ? undefined : triggerInstall}
                  onNavigateToRewards={() => setActiveTab('rewards')}
                />
              )}
              {activeTab === 'transfer' && (
                <div className="h-full flex flex-col">
                  {/* 브릿지는 하단 네비에서 빠져 지갑 안 하위 탭으로 (2026-10-01) */}
                  <div className="px-4 pt-4 pb-2">
                    <div style={{ display: 'flex', background: '#E9ECF1', borderRadius: 999, padding: 4 }}>
                      {([['wallet', '지갑'], ['bridge', '브릿지']] as const).map(([k, label]) => (
                        <button key={k} onClick={() => setWalletSub(k)}
                          style={{ flex: 1, padding: '10px 0', borderRadius: 999, fontSize: 14,
                                   fontWeight: 700, border: 0, transition: 'all .15s',
                                   ...(walletSub === k
                                     ? { background: '#FFFFFF', color: '#111827',
                                         boxShadow: '0 1px 4px rgba(17,24,39,.14)' }
                                     : { background: 'transparent', color: '#6B7280' }) }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto">
                    {walletSub === 'wallet' && (
                      <>
                      <WalletTransfer
                        user={user}
                        transactions={transactions}
                        onUpdateBalance={updateBalance}
                        onAddTransaction={addTransaction}
                      />
                      </>
                    )}
                    {walletSub === 'bridge' && <BridgePage user={user} />}
                  </div>
                </div>
              )}
              {activeTab === 'staking' && (
                <Staking 
                  user={user}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                />
              )}
              {activeTab === 'game' && (
                <GameArena authHeaders={authHeaders} />
              )}
              {activeTab === 'events' && (
                <Events 
                  user={user}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                  eventSettings={eventSettings}
                  onTabChange={setActiveTab}
                />
              )}
              {activeTab === 'bridge' && (
                <BridgePage user={user} />
              )}
              {activeTab === 'rewards' && (
                <Rewards 
                  user={user}
                  onSettingsClick={() => setActiveTab('settings')}
                />
              )}
              {activeTab === 'settings' && (
                <Settings 
                  user={user}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  onToggleDarkMode={setDarkMode}
                  isAdmin={false}
                  onAdminLogin={(password: string) => false}
                  eventSettings={eventSettings}
                  onEventSettingsUpdate={handleEventSettingsUpdate}
                />
              )}

            </div>

            {/* Bottom Navigation - Fixed */}
            <div className="fixed bottom-0 left-0 right-0 h-20 bg-background border-t border-border flex items-center justify-around px-2 z-50" style={{ maxWidth: '100vw' }}>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'dashboard' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Home size={18} />
                <span className="text-xs">{i18n.t('home')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('transfer')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'transfer' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Wallet size={18} />
                <span className="text-xs">{i18n.t('wallet')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('game')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'game'
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                <Gamepad2 size={18} />
                <span className="text-xs">{i18n.t('game') || '게임'}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('staking')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'staking' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <TrendingUp size={18} />
                <span className="text-xs">{i18n.t('staking')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('events')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'events'
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                <Calendar size={18} />
                <span className="text-xs">{i18n.t('events')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('rewards')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'rewards' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Gift size={18} />
                <span className="text-xs">{i18n.t('rewards')}</span>
              </button>
              
            </div>
          </div>
        </div>
      </div>
      
      
      {/* 앱 설치 버튼 - 설치 가능할 때 상시 노출(하단 네비 위에 고정)
          [2026-09-12] 인앱 브라우저는 beforeinstallprompt 자체가 안 와서 버튼이
          아예 안 보였다. 인앱일 때도 버튼을 띄워 외부 브라우저로 유도한다. */}
      {(installPrompt || detectInAppBrowser()) && !isStandalone && (
        <button
          onClick={triggerInstall}
          className="fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-sm font-bold shadow-lg"
          style={{ bottom: '84px', background: '#FF6B00' }}
        >
          <Smartphone size={16} />
          앱 설치하기
        </button>
      )}

      {/* 카톡 등 인앱 브라우저로 들어온 경우 설치 방법 안내 팝업 */}
      <InAppBrowserNotice />
      <Toaster />
      <SignupCompletePopup
        isOpen={showSignupComplete}
        onClose={() => {
          localStorage.removeItem('handy-signup-bonus-popup');
          setShowSignupComplete(false);
        }}
        bonusAmount={signupBonusAmount}
      />
    </>
  );
}